import type { SVGProps } from 'react';

import { cn } from '@/lib/cn';

/**
 * Small inline icon set. Inline SVG keeps the site dependency-free and lets
 * every glyph inherit `currentColor` from its container.
 */
const PATHS = {
  revenue: 'M12 2v20M17 5.5A4.5 4.5 0 0 0 12.5 2h-1a4.5 4.5 0 0 0 0 9h1a4.5 4.5 0 0 1 0 9h-1A4.5 4.5 0 0 1 7 16.5',
  setup:
    'M10.3 3.2a1 1 0 0 1 1-.8h1.4a1 1 0 0 1 1 .8l.3 1.6 1.5.9 1.6-.5a1 1 0 0 1 1.2.4l.7 1.2a1 1 0 0 1-.2 1.3l-1.2 1v1.8l1.2 1a1 1 0 0 1 .2 1.3l-.7 1.2a1 1 0 0 1-1.2.4l-1.6-.5-1.5.9-.3 1.6a1 1 0 0 1-1 .8h-1.4a1 1 0 0 1-1-.8l-.3-1.6-1.5-.9-1.6.5a1 1 0 0 1-1.2-.4l-.7-1.2a1 1 0 0 1 .2-1.3l1.2-1v-1.8l-1.2-1a1 1 0 0 1-.2-1.3l.7-1.2a1 1 0 0 1 1.2-.4l1.6.5 1.5-.9zM12 9.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z',
  support: 'M4 13a8 8 0 0 1 16 0M4 13v3a2 2 0 0 0 2 2h1v-5H6a2 2 0 0 0-2 2zm16 0v3a2 2 0 0 1-2 2h-1v-5h1a2 2 0 0 1 2 2z',
  remote: 'M5 12.5a10 10 0 0 1 14 0M8 15.5a6 6 0 0 1 8 0M12 19h.01M2 9.5a15 15 0 0 1 20 0',
  free: 'M20 7H4a1 1 0 0 0-1 1v3h18V8a1 1 0 0 0-1-1zM3 11v6a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1v-6M12 7v11M12 7S9 3.5 7.5 5 12 7 12 7zm0 0s3-3.5 4.5-2S12 7 12 7z',
  secure: 'M12 3l7 3v5.5c0 4.4-2.9 8.4-7 9.5-4.1-1.1-7-5.1-7-9.5V6l7-3zM9.5 12l1.8 1.8 3.4-3.6',
  touch: 'M9 11V6a1.5 1.5 0 1 1 3 0v5m0-1.5a1.5 1.5 0 0 1 3 0V12m0-1a1.5 1.5 0 0 1 3 0v4.5a5.5 5.5 0 0 1-5.5 5.5h-1a5.5 5.5 0 0 1-4.6-2.5L5 15.5a1.6 1.6 0 0 1 2.6-1.9L9 15.5',
  shield: 'M12 3l7 3v5.5c0 4.4-2.9 8.4-7 9.5-4.1-1.1-7-5.1-7-9.5V6l7-3z',
  wifi: 'M5 12.5a10 10 0 0 1 14 0M8 15.5a6 6 0 0 1 8 0M12 19h.01M2 9.5a15 15 0 0 1 20 0',
  lock: 'M6 11h12v9H6zM9 11V8a3 3 0 0 1 6 0v3',
  size: 'M4 9V5a1 1 0 0 1 1-1h4M20 15v4a1 1 0 0 1-1 1h-4M4 15v4a1 1 0 0 1 1 1h4M20 9V5a1 1 0 0 0-1-1h-4',
  link: 'M10 13a4 4 0 0 0 5.7.4l3-3A4 4 0 1 0 13 4.8l-1.7 1.7M14 11a4 4 0 0 0-5.7-.4l-3 3A4 4 0 1 0 11 19.2l1.7-1.7',
  play: 'M8 5.5v13l11-6.5z',
  mail: 'M3 6.5h18v11H3zM3 7l9 6 9-6',
  phone: 'M6.5 3.5h3l1.5 4-2 1.5a12 12 0 0 0 6 6l1.5-2 4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.5 5.7a2 2 0 0 1 2-2.2z',
  check: 'M4.5 12.5l5 5 10-11',
  arrow: 'M5 12h14M13 6l6 6-6 6',
  menu: 'M4 7h16M4 12h16M4 17h16',
  close: 'M6 6l12 12M18 6L6 18',
  store: 'M4 9.5L5.5 5h13L20 9.5M4 9.5h16M4 9.5V19h16V9.5M9.5 19v-5h5v5',
  spark: 'M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9zM18.5 15.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z',
  users: 'M16 19v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 17.5V19m6.5-9.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM20 19v-1.5a3.5 3.5 0 0 0-2.6-3.4M15.5 4.1a3 3 0 0 1 0 5.8',
  megaphone: 'M4 10v4a1 1 0 0 0 1 1h2l7 4V5L7 9H5a1 1 0 0 0-1 1zm14-2a4.5 4.5 0 0 1 0 8',
  pin: 'M12 21s7-5.2 7-11a7 7 0 1 0-14 0c0 5.8 7 11 7 11zm0-8.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  gamepad: 'M7 11h4M9 9v4m5-1h.01M16.5 10.5h.01M7.5 6h9a4.5 4.5 0 0 1 4.4 3.6l.7 5A3.5 3.5 0 0 1 18.2 19c-1 0-1.9-.5-2.5-1.3l-.9-1.2H9.2l-.9 1.2A3.1 3.1 0 0 1 5.8 19a3.5 3.5 0 0 1-3.4-4.4l.7-5A4.5 4.5 0 0 1 7.5 6z',
  tablet: 'M6 3h12a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm5 15h2',
  monitor: 'M3 5h18v11H3zM9 20h6M12 16v4',
  paint: 'M4 7.5A3.5 3.5 0 0 1 7.5 4H16a1 1 0 0 1 1 1v3H7.5A3.5 3.5 0 0 1 4 7.5zm13 .5h2a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-6v2.5a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1V13a2 2 0 0 1 2-2h5',
  truck: 'M3 7h10v9H3zM13 10h4l3 3v3h-7zM7.5 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm10 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z',
} as const;

export type IconName = keyof typeof PATHS;

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  className?: string;
}

export function Icon({ name, className, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cn('h-6 w-6 shrink-0', className)}
      {...props}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
